import React, { useMemo, useState } from "react";

type FormState = {
  name: string;
  email: string;
  subject: string;
  message: string;
  "bot-field"?: string; // honeypot
};

const Contact: React.FC = () => {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    subject: "",
    message: "",
    "bot-field": "",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const bookingEmail = "colleenlavincomedy@gmail.com"; // swap to your domain email when ready
  const formName = "contact";

  // Netlify expects URL-encoded form bodies for JS submissions
  const encodedBody = useMemo(() => {
    const params = new URLSearchParams();
    params.append("form-name", formName);
    Object.entries(form).forEach(([key, value]) => {
      params.append(key, value ?? "");
    });
    return params.toString();
  }, [form]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If bot-field is filled, silently succeed (spam)
    if (form["bot-field"]) {
      setStatus("success");
      return;
    }

    try {
      setStatus("submitting");

      // IMPORTANT: This posts to your own site; Netlify intercepts it when deployed on Netlify
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encodedBody,
      });

      if (!res.ok) throw new Error(`Non-200 response: ${res.status}`);

      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <section className="page contact">
        <div className="container">
          <h2>Thank You</h2>
          <p className="contact-intro">
            Your message has been received. I’ll get back to you soon.
          </p>
          <p className="contact-intro">
            If you don’t hear back within a few days, feel free to email me directly at{" "}
            <a href={`mailto:${bookingEmail}`}>{bookingEmail}</a>.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="page contact">
      <div className="container">
        <h2>Get in Touch</h2>
        <p className="contact-intro">For booking inquiries, press requests, or just to say hello.</p>

        {/* Netlify Forms: data-netlify + hidden form-name + honeypot */}
        <form
          className="contact-form"
          name={formName}
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
        >
          <input type="hidden" name="form-name" value={formName} />

          {/* Honeypot field (hidden) */}
          <p style={{ display: "none" }}>
            <label>
              Don’t fill this out:{" "}
              <input name="bot-field" value={form["bot-field"] || ""} onChange={handleChange} />
            </label>
          </p>

          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input id="name" name="name" type="text" value={form.name} onChange={handleChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <select id="subject" name="subject" value={form.subject} onChange={handleChange} required>
              <option value="">Select a subject</option>
              <option value="booking">Booking Inquiry</option>
              <option value="press">Press Request</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={6}
              required
            />
          </div>

          <button className="btn" type="submit" disabled={status === "submitting"}>
            {status === "submitting" ? "Sending…" : "Send message"}
          </button>

          {status === "error" && (
            <p className="contact-intro" role="alert">
              Something went wrong sending your message. Please email me at{" "}
              <a href={`mailto:${bookingEmail}`}>{bookingEmail}</a>.
            </p>
          )}

          <p className="contact-intro" style={{ marginTop: "1rem" }}>
            Prefer email? <a href={`mailto:${bookingEmail}`}>{bookingEmail}</a>
          </p>
        </form>
      </div>
    </section>
  );
};

export default Contact;
